// -----------------------------------------------------------------------
// <copyright file="eduConfig.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { InputType, InputField } from '../models/typings/inputFields';
import { EduFormField, EduKeyValueMap, EduProductId } from './eduConstants';

export type EduProduct = {
  name: string;
  additionalFields?: EduFormField[];
};

export type EduConfiguration = {
  inputFields: { [key in EduFormField]?: InputField };
  commonFields: EduFormField[];
  products: { [key: string]: EduProduct };
};

const eduProducts: { [key: string]: EduProduct } = {
  [EduProductId.HackingStem]: {
    name: 'HackingSTEM',
  },
  [EduProductId.ImmersiveReader]: {
    name: 'Immersive Reader',
  },
  [EduProductId.ClassInsights]: {
    name: 'Insights',
  },
  [EduProductId.LtiApps]: {
    name: 'LTI apps',
    additionalFields: [EduFormField.ProductFeatureArea, EduFormField.LMS],
  },
  [EduProductId.MathAssistant]: {
    name: 'Math Assistant',
  },
  [EduProductId.MathCoach]: {
    name: 'Math Coach/Progress',
  },
  [EduProductId.Edge]: {
    name: 'Microsoft Edge',
  },
  [EduProductId.Forms]: {
    name: 'Microsoft Forms',
  },
  [EduProductId.Stream]: {
    name: 'Microsoft Stream',
  },
  [EduProductId.Teams]: {
    name: 'Microsoft Teams',
  },
  [EduProductId.Whiteboard]: {
    name: 'Microsoft Whiteboard',
  },
  [EduProductId.MieExpertProgram]: {
    name: 'MIE Expert Program',
  },
  [EduProductId.MinecraftEducation]: {
    name: 'Minecraft: Education Edition',
  },
  [EduProductId.ClassNotebook]: {
    name: 'OneNote Class/Staff Notebook',
  },
  [EduProductId.OpenUp]: {
    name: 'Open Up Resources',
  },
  [EduProductId.SuccessSeries]: {
    name: 'Partner Success Series',
  },
  [EduProductId.ReadingProgress]: {
    name: 'Reading Progress',
  },
  [EduProductId.Reflect]: {
    name: 'Reflect',
  },
  [EduProductId.SchoolConnection]: {
    name: 'School Connection',
  },
  [EduProductId.SchoolDataSync]: {
    name: 'School Data Sync',
    additionalFields: [EduFormField.NewSdsCustomer],
  },
  [EduProductId.SearchCoach]: {
    name: 'Search Coach',
  },
  [EduProductId.SpeakerProgress]: {
    name: 'Speaker Coach/Progress',
  },
  [EduProductId.Win11SeSupport]: {
    name: 'Windows 11 SE',
  },
 
};

export const EduConfig: EduConfiguration = {
  inputFields: {
    [EduFormField.Title]: {
      id: EduFormField.Title,
      inputType: InputType.Text,
      labelStringId: 'requestCreation_issueSummaryLabel',
      placeholderStringId: 'requestCreation_issueSummaryPlaceholder',
      isRequired: true,
    },
    [EduFormField.Description]: {
      id: EduFormField.Description,
      inputType: InputType.TextArea,
      labelStringId: 'requestCreation_issueDescriptionLabel',
      placeholderStringId: 'requestCreation_issueDescriptionPlaceholder',
      isRequired: true,
    },
    [EduFormField.PreferredEmail]: {
      id: EduFormField.PreferredEmail,
      inputType: InputType.Text,
      labelStringId: 'requestCreation_emailLabel',
      placeholderStringId: 'requestCreation_emailPlaceholder',
      isRequired: true,
    },
    [EduFormField.UserImpact]: {
      id: EduFormField.UserImpact,
      inputType: InputType.Dropdown,
      labelStringId: 'requestCreation_userImpactText',
      placeholderStringId: 'requestCreation_dropDownSelectAnOptionText',
      isRequired: true,
      options: [
        {
          value: 'One User',
          textStringId: 'requestCreation_userImpactQuantification_oneUser',
        },
        {
          value: 'Some Users',
          textStringId: 'requestCreation_userImpactQuantification_someUsers',
        },
        {
          value: 'Many Users',
          textStringId: 'requestCreation_userImpactQuantification_manyUsers',
        },
        {
          value: 'Entire Organization',
          textStringId: 'requestCreation_userImpactQuantification_entireOrganization',
        },
        {
          value: 'Not Sure',
          textStringId: 'requestCreation_userImpactQuantification_notSure',
        },
      ],
    },
    [EduFormField.NewSdsCustomer]: {
      id: EduFormField.NewSdsCustomer,
      inputType: InputType.Checkbox,
      labelStringId: 'requestCreation_newSdsCustomerCheckboxLabel',
      isRequired: false,
    },
    [EduFormField.ProductFeatureArea]: {
      id: EduFormField.ProductFeatureArea,
      inputType: InputType.Dropdown,
      labelStringId: 'requestCreation_ltiProductFeatureAreaLabel',
      placeholderStringId: 'requestCreation_dropDownSelectAnOptionText',
      isRequired: true,
      options: [
        {
          value: 'onedrive',
          text: 'OneDrive',
        },
        {
          value: 'teams_classes',
          text: 'Teams Classes',
        },
        {
          value: 'teams_meetings',
          text: 'Teams Meetings',
        },
        {
          value: 'assignments_lti',
          text: 'Assignments LTI',
        },
      ],
    },
    [EduFormField.LMS]: {
      id: EduFormField.LMS,
      inputType: InputType.Dropdown,
      labelStringId: 'requestCreation_lmsLabel',
      placeholderStringId: 'requestCreation_dropDownSelectAnOptionText',
      isRequired: true,
      options: Object.keys(EduKeyValueMap.LmsNames).map(key => {
        return { 
          value: key,
          text: EduKeyValueMap.LmsNames[key]
        }
      })
    },
    [EduFormField.Product]: {
      id: EduFormField.Product,
      inputType: InputType.Dropdown,
      labelStringId: 'requestCreation_eduProductSelectionLabel',
      placeholderStringId: 'requestCreation_dropDownSelectAnOptionText',
      isRequired: true,
      options: Object.keys(eduProducts).map(productId => {
        return {
          value: productId,
          text: eduProducts[productId].name,
        };
      }),
    },
  },
  commonFields: [
    EduFormField.Product,
    EduFormField.Title,
    EduFormField.Description,
    EduFormField.Attachments,
    EduFormField.UserImpact,
    EduFormField.PreferredEmail,
  ],
  products: eduProducts,
};
